import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { FileText, ChevronDown, ChevronUp, Edit, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { log } from '@/lib/log';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Templates = () => {
  const [templates, setTemplates] = useState([]);
  const [expandedTemplate, setExpandedTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', content: '', placeholders: [] });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data.templates || []);
    } catch (error) {
      log.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template.id);
    setEditForm({
      name: template.name,
      content: template.content,
      placeholders: template.placeholders
    });
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/templates/${editingTemplate}`, {
        template_id: editingTemplate,
        ...editForm
      });
      toast.success('Template updated successfully!');
      setEditingTemplate(null);
      fetchTemplates();
    } catch (error) {
      log.error('Failed to update template:', error);
      toast.error('Failed to update template');
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-xl p-6 border border-gray-100 shimmer h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="templates-page">
      <div className="mb-8">
        <h1 className="text-4xl font-semibold text-google-text-primary tracking-tight">
          Document Templates
        </h1>
        <p className="text-google-text-secondary mt-2">
          {templates.length} templates available - Click Edit to customize content
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {templates.map((template) => {
          const isExpanded = expandedTemplate === template.id;
          return (
            <div
              key={template.id}
              data-testid={`template-card-${template.id}`}
              className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-50 rounded-xl">
                    <FileText className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-google-text-primary mb-1">
                      {template.name}
                    </h3>
                    <p className="text-sm text-google-text-secondary">
                      {template.placeholders?.length || 0} fields required
                    </p>
                  </div>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(template)}
                        data-testid={`edit-template-${template.id}`}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Edit Template: {template.name}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <label className="text-sm font-medium mb-2 block">Template Name</label>
                          <Input
                            value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-2 block">
                            Template Content (Use {'{FieldName}'} for placeholders)
                          </label>
                          <Textarea
                            value={editForm.content}
                            onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                            rows={15}
                            className="font-mono text-sm"
                          />
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" onClick={() => setEditingTemplate(null)}>
                            <X className="w-4 h-4 mr-1" />
                            Cancel
                          </Button>
                          <Button onClick={handleSave}>
                            <Save className="w-4 h-4 mr-1" />
                            Save Changes
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  data-testid={`expand-template-${template.id}`}
                  onClick={() => setExpandedTemplate(isExpanded ? null : template.id)}
                  className="mt-4 w-full justify-between"
                >
                  <span>View Fields</span>
                  {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>

                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="text-sm font-medium text-google-text-primary mb-2">
                      Required Fields:
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {template.placeholders?.map((placeholder) => (
                        <span
                          key={placeholder}
                          className="px-3 py-1 bg-gray-100 text-google-text-secondary rounded-full text-xs"
                        >
                          {placeholder.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Templates;